// ---------------------------------------------------------------------------
// SpeedyDock SDK — Main entry point
//
// Usage (simplest):
//   <script src="https://bookings.example.com/sdk/speedydock.js"
//           data-shortname="lakeside-marina"></script>
//
// Usage (programmatic):
//   SpeedyDock.init({ shortName: 'lakeside-marina' });
//   SpeedyDock.modal({ trigger: '#book-now' });
// ---------------------------------------------------------------------------

import type {
  SpeedyDockConfig,
  InlineOptions,
  ModalOptions,
  DrawerOptions,
  RedirectOptions,
  SearchWidgetOptions,
  BoatCardOptions,
  LaunchOptions,
  EmbedInstance,
} from "./types";
import { injectStyles, applyTheme } from "./styles";
import { detectBaseUrl, detectAutoConfig, createElement } from "./utils";
import { SDK_PREFIX } from "./styles";
import { createInlineEmbed } from "./inline";
import { createModalEmbed, openModal } from "./modal";
import { createDrawerEmbed } from "./drawer";
import { createRedirectEmbed } from "./redirect";
import { createSearchWidget } from "./search-widget";
import { createBoatCard } from "./boat-card";

// ---------------------------------------------------------------------------
// State
// ---------------------------------------------------------------------------

let config: SpeedyDockConfig | null = null;
let baseUrl = "";
const instances: { destroy(): void }[] = [];

function ensureInit(): { baseUrl: string; shortName: string; defaults: { conciergeId?: number; locationId?: number; categoryId?: number | string; primaryColor?: string } } {
  if (!config) {
    throw new Error(
      "[SpeedyDock] SDK not initialized. Call SpeedyDock.init({ shortName: '...' }) first.",
    );
  }
  return { baseUrl, shortName: config.shortName, defaults: { conciergeId: config.conciergeId, locationId: config.locationId, categoryId: config.categoryId, primaryColor: config.theme?.primaryColor } };
}

/** Merge config-level defaults into per-call options (per-call wins). */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function withDefaults<T>(defaults: { conciergeId?: number; locationId?: number; categoryId?: number | string; primaryColor?: string }, opts: T): T {
  const d = defaults as Record<string, unknown>;
  const o = opts as Record<string, unknown>;
  return {
    ...opts,
    conciergeId: o.conciergeId ?? d.conciergeId,
    locationId: o.locationId ?? d.locationId,
    categoryId: o.categoryId ?? d.categoryId,
    primaryColor: o.primaryColor ?? d.primaryColor,
  } as T;
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

const SpeedyDock = {
  /**
   * Initialize the SDK with configuration.
   */
  init(cfg: SpeedyDockConfig): void {
    config = cfg;
    baseUrl = cfg.baseUrl ?? detectBaseUrl();
    injectStyles();
    applyTheme(cfg.theme);
  },

  /**
   * Embed the booking experience inline into a target container.
   */
  inline(
    target: string | HTMLElement,
    options: InlineOptions = {},
  ): EmbedInstance {
    const ctx = ensureInit();
    const inst = createInlineEmbed(ctx.baseUrl, ctx.shortName, target, withDefaults(ctx.defaults, options));
    instances.push(inst);
    return inst;
  },

  /**
   * Attach a modal/popup launcher to a trigger element.
   * If no trigger is provided, opens immediately.
   */
  modal(options: ModalOptions = {}): EmbedInstance {
    const ctx = ensureInit();
    const merged = withDefaults(ctx.defaults, options);
    const inst = merged.trigger
      ? createModalEmbed(ctx.baseUrl, ctx.shortName, merged)
      : openModal(ctx.baseUrl, ctx.shortName, merged);
    instances.push(inst);
    return inst;
  },

  /**
   * Attach a side drawer to a trigger element.
   */
  drawer(options: DrawerOptions = {}): EmbedInstance {
    const ctx = ensureInit();
    const inst = createDrawerEmbed(ctx.baseUrl, ctx.shortName, withDefaults(ctx.defaults, options));
    instances.push(inst);
    return inst;
  },

  /**
   * Attach a full-page redirect to a trigger element.
   */
  redirect(options: RedirectOptions = {}): EmbedInstance {
    const ctx = ensureInit();
    const inst = createRedirectEmbed(ctx.baseUrl, ctx.shortName, withDefaults(ctx.defaults, options));
    instances.push(inst);
    return inst;
  },

  /**
   * Render a small availability/search widget.
   */
  searchWidget(
    target: string | HTMLElement,
    options: SearchWidgetOptions = {},
  ): { destroy(): void } {
    const ctx = ensureInit();
    const mode = options.mode ?? "modal";

    const openBooking = (date?: string) => {
      const opts = withDefaults(ctx.defaults, { date });
      if (mode === "modal") openModal(ctx.baseUrl, ctx.shortName, opts);
      else if (mode === "drawer")
        createDrawerEmbed(ctx.baseUrl, ctx.shortName, {
          ...opts,
          width: options.width,
          position: options.position,
        });
      else if (mode === "redirect")
        createRedirectEmbed(ctx.baseUrl, ctx.shortName, opts).navigate("");
    };

    const inst = createSearchWidget(openBooking, target, options);
    instances.push(inst);
    return inst;
  },

  /**
   * Render a boat preview card widget.
   */
  boatCard(
    target: string | HTMLElement,
    options: BoatCardOptions,
  ): { destroy(): void } {
    const ctx = ensureInit();
    const mode = options.mode ?? "modal";

    const openBooking = (boatSlug: string, date?: string) => {
      const opts = withDefaults(ctx.defaults, { boatSlug, date });
      if (mode === "modal") openModal(ctx.baseUrl, ctx.shortName, opts);
      else if (mode === "drawer")
        createDrawerEmbed(ctx.baseUrl, ctx.shortName, opts);
      else if (mode === "redirect")
        createRedirectEmbed(ctx.baseUrl, ctx.shortName, opts).navigate("");
    };

    const inst = createBoatCard(openBooking, target, options);
    instances.push(inst);
    return inst;
  },

  /**
   * Multi-step booking launcher — deep link into a specific boat, date,
   * location, or step in any embed mode.
   */
  launch(options: LaunchOptions = {}): EmbedInstance | { destroy(): void } {
    const ctx = ensureInit();
    const mode = options.mode ?? "modal";

    const opts = withDefaults(ctx.defaults, {
      date: options.date,
      boatSlug: options.boatSlug,
      locationId: options.locationId,
      categoryId: options.categoryId,
      conciergeId: options.conciergeId,
    });

    switch (mode) {
      case "inline": {
        const target = options.target;
        if (!target)
          throw new Error("[SpeedyDock] launch({ mode: 'inline' }) requires a target.");
        return SpeedyDock.inline(target, opts);
      }
      case "modal":
        return openModal(ctx.baseUrl, ctx.shortName, opts);
      case "drawer":
        return SpeedyDock.drawer(opts);
      case "redirect":
        return SpeedyDock.redirect({ ...opts, target: "_self" });
      default:
        return openModal(ctx.baseUrl, ctx.shortName, opts);
    }
  },

  /**
   * Destroy all active instances and clean up.
   */
  destroyAll(): void {
    instances.forEach((i) => i.destroy());
    instances.length = 0;
  },
};

// ---------------------------------------------------------------------------
// Auto-initialization from script tag data attributes
// ---------------------------------------------------------------------------

function autoInit(): void {
  const auto = detectAutoConfig();
  if (!auto?.shortName) return;

  SpeedyDock.init({ shortName: auto.shortName, conciergeId: auto.conciergeId, locationId: auto.locationId, categoryId: auto.categoryId });

  // Create a floating "Book Now" button
  injectStyles();
  const fab = createElement("button", `${SDK_PREFIX}-fab`);
  fab.textContent = "Book Now ⛵";
  fab.setAttribute("aria-label", "Open boat rental booking");
  document.body.appendChild(fab);

  fab.addEventListener("click", () => {
    SpeedyDock.modal();
  });
}

// Run auto-init when DOM is ready
if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", autoInit);
} else {
  autoInit();
}

// Expose globally
declare global {
  interface Window {
    SpeedyDock: typeof SpeedyDock;
  }
}
window.SpeedyDock = SpeedyDock;

export default SpeedyDock;
export type { SpeedyDockConfig, EmbedInstance };
